/**
 * Awards — points summary, the award-level ladder and the earn rules
 * (mobile drawer/awards parity).
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useAwards from '@wlb/shared/hooks/useAwards';
import { awardIconKeyToLevel } from '@wlb/shared/utils/progress';
import type { AwardLevelItem } from '@wlb/shared/types/userProfile';
import { ListStatus } from '../components/ui/ListStatus';
import {
  AWARD_LEVEL_ARTWORK,
  AWARD_RULE_ARTWORK,
} from '../features/more/awardArtwork';
import { formatAwardPoints } from '../features/more/awardPoints';
import { awardRuleIconKey } from '../features/more/awardRuleIcon';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import '../features/more/more.css';

const levelPointsLabel = (
  level: AwardLevelItem,
  t: (key: string, params?: Record<string, string | number>) => string,
): string =>
  level.maxPoints
    ? t('awards.pointsRange', { min: level.minPoints, max: level.maxPoints })
    : t('awards.pointsRangeOpen', { min: level.minPoints });

export function Awards() {
  const { t } = useTranslation();
  const { summary, levels, earnRules, loading, error, loaded, refresh } = useAwards();

  if (loading && !summary && levels.length === 0) {
    return (
      <MorePage title={t('awards.title')}>
        <MoreLoading variant="card" />
      </MorePage>
    );
  }

  if (error && !summary) {
    return (
      <MorePage title={t('awards.title')}>
        <ListStatus variant="error" message={error} onAction={refresh} busy={loading} />
      </MorePage>
    );
  }

  if (loaded && !summary && levels.length === 0) {
    return (
      <MorePage title={t('awards.title')}>
        <ListStatus
          variant="empty"
          title={t('awards.empty.title')}
          message={t('awards.empty.description')}
          onAction={refresh}
          busy={loading}
        />
      </MorePage>
    );
  }

  const totalPoints = summary?.totalPoints ?? 0;
  const progressPercent = Math.max(0, Math.min(100, summary?.progressPercent ?? 0));

  return (
    <MorePage title={t('awards.title')}>
      <section className="awards-summary">
        <p className="awards-summary__label">{t('awards.yourTotalPoints')}</p>
        <p className="awards-summary__points">{totalPoints.toLocaleString()}</p>
        <div className="awards-summary__levels">
          <span>{t('awards.currentLevelLabel', { level: summary?.currentLevelName ?? '' })}</span>
          {summary?.nextLevelName && (
            <span>{t('awards.nextLevelLabel', { level: summary.nextLevelName })}</span>
          )}
        </div>
        <div
          className="awards-summary__track"
          role="progressbar"
          aria-label={t('awards.accessibility.progress')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progressPercent)}
        >
          <span className="awards-summary__fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <p className="awards-summary__motivation">{t('awards.motivation')}</p>
      </section>

      {levels.length > 0 && (
        <>
          <h2 className="more-section-title">{t('awards.awardLevels')}</h2>
          <ul className="awards-grid" aria-label={t('awards.accessibility.levels')}>
            {levels.map(level => {
              const levelKey = awardIconKeyToLevel(level.iconKey);
              return (
                <li
                  key={`${level.levelName ?? levelKey}-${level.minPoints}`}
                  className={`awards-level${level.isCurrent ? ' awards-level--current' : ''}`}
                >
                  <span className="awards-level__icon" aria-hidden>
                    <img src={AWARD_LEVEL_ARTWORK[levelKey]} alt="" />
                  </span>
                  <span className="awards-level__name">{level.levelName}</span>
                  <span className="awards-level__points">{levelPointsLabel(level, t)}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {earnRules.length > 0 && (
        <>
          <h2 className="more-section-title">{t('awards.howToEarn')}</h2>
          <section className="more-card">
            {earnRules.map(rule => {
              const artwork = AWARD_RULE_ARTWORK[awardRuleIconKey(rule.ruleFor)];
              return (
                <div className="awards-rule" key={`${rule.ruleName}-${rule.points}`}>
                  <span className="awards-rule__icon" aria-hidden>
                    <img src={artwork} alt="" />
                  </span>
                  <span className="awards-rule__copy">
                    <span className="awards-rule__name">{rule.ruleName}</span>
                    {rule.ruleDescription && (
                      <span className="awards-rule__description">{rule.ruleDescription}</span>
                    )}
                  </span>
                  <span className="awards-rule__points">
                    {t('awards.pointsValue', {
                      points: formatAwardPoints(rule.points),
                      unit: t('awards.earn.pts'),
                    })}
                  </span>
                </div>
              );
            })}
          </section>
        </>
      )}
    </MorePage>
  );
}
